import { ChangeDetectorRef, Component, inject, PLATFORM_ID } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  BaseSiteService,
  GlobalMessageService,
  GlobalMessageType,
  LanguageService,
} from '@spartacus/core';
import { CookieService } from 'src/app/customServices/cookie.service';
import { RegisterService } from 'src/app/services/feature/register.service';
import { DataProtectionComponent } from './data-protection/data-protection.component';
import { GeneralConditionsComponent } from './general-conditions/general-conditions.component';
import { SoldToInfoComponent } from './sold-to-info/sold-to-info.component';
import { isPlatformBrowser } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { SessionMessageStorageService } from '../../services/feature/session-message/kb-session-message.service';

@Component({
  selector: 'app-kb-register',
  templateUrl: './kb-register.component.html',
  styleUrls: ['./kb-register.component.scss'],
  standalone: false
})
export class KbRegisterComponent {
  submitted = false;
  isWorking = false;
  pageFieldData: any;
  accessRequestsWSList: any;
  countriesWSList: any;
  stateList: any;
  token: string | undefined;
  selectedIndustryRole: string;
  selectedRequestAccess: any = [];
  captchaResolved: boolean = false;
  baseSite: string;
  isUserAlreadyRegistered: any;
  pageFieldData$ = this.registerService.getRegisterPageFields();
  filteredIndustryRoles: any;
  platformId = inject(PLATFORM_ID);
  currentUrl: string;
  mexicoDisable: boolean = false;
  private destroy$ = new Subject<void>();
  private timeout?: ReturnType<typeof setTimeout> | null = null;// Store the timeout ID
  private messageSessionService = inject(SessionMessageStorageService);
  constructor(
    private registerService: RegisterService,
    private dialog: MatDialog,
    private baseSiteService: BaseSiteService,
    private cdf: ChangeDetectorRef,
    private router: Router,
    private langservice: LanguageService,
    private globalMessageService: GlobalMessageService,
    private coookieService: CookieService
  ) {
    this.token = undefined;
    this.currentUrl = isPlatformBrowser(this.platformId) ? window.location.href : '';
    if (this.currentUrl.includes('MX')) {
      this.mexicoDisable = true;
    }
  }

  signupForm = new FormGroup(
    {
      email: new FormControl(null, [Validators.email, Validators.required]),
      industry: new FormControl(null),
      firstName: new FormControl(null, [Validators.required]),
      lastName: new FormControl(null, [Validators.required]),
      title: new FormControl(null),
      companyName: new FormControl(null, [Validators.required]),
      soldTo: new FormControl(null),
      requestAccessFor: new FormControl(null),
      address1: new FormControl(null, [Validators.required]),
      address2: new FormControl(null),
      city: new FormControl(null, [Validators.required]),
      zip: new FormControl(null, [Validators.required]),
      country: new FormControl(null, [Validators.required]),
      state: new FormControl(null, [Validators.required]),
      phone: new FormControl(null),
      fax: new FormControl(null),
      website: new FormControl(null),
      addSoldTo: new FormControl(null),
      recaptcha: new FormControl(null, [Validators.required]),
      generalCondition: new FormControl(false, [Validators.requiredTrue]),
      dataProtection: new FormControl(false, [Validators.requiredTrue]),
      password: new FormControl(
        null,
        Validators.compose([
          Validators.required,
          Validators.minLength(12),
          this.patternValidator(new RegExp('(?=.*[0-9])'), {
            requiresDigit: true,
          }),
          this.patternValidator(new RegExp('(?=.*[A-Z])'), {
            requiresUppercase: true,
          }),
          this.patternValidator(new RegExp('(?=.*[a-z])'), {
            requiresLowercase: true,
          }),
          this.patternValidator(new RegExp("(?=.*[()~!@#$%^&*\\-_+=`|{}\\[\\]:;\"'<>,?/])"), {
            requiresSpecialChars: true,
          }),
        ])
      ),
      confirmPassword: new FormControl(null, [
        Validators.required,
        Validators.minLength(12),
      ]),
    },
    {
      validators: this.MatchValidator,
    }
  );

  ngOnInit() {
    this.langservice.getActive().subscribe((data) => {
      this.baseSiteService.getActive().subscribe((siteName) => {
        this.baseSite = siteName;
        this.registerService.getRegisterPageFields().subscribe((data) => {
          this.pageFieldData = data;
          this.accessRequestsWSList =
            this.pageFieldData.accessRequestsWSList.filter(
              (item) => item.code != 'DEALER_ADB'
            );
          if (this.coookieService.getCookieValue('countryIsoCode') == 'MX') {
            this.accessRequestsWSList =
              this.pageFieldData.accessRequestsWSList.filter(
                (item) => item.code === 'ORDER_ONLINE'
              );
          }
          this.countriesWSList = this.pageFieldData.countriesWSList;
          this.signupForm.controls['title'].setValue('');
          this.applyIndustryRoleFilter();
          this.cdf.detectChanges();
        });
      });
    });
  }


  // convenience getter for easy access to form controls
  get f() {
    return this.signupForm.controls;
  }

  get passwordValid() {
    return this.signupForm.controls['password'].errors === null;
  }

  get requiredValid() {
    return !this.signupForm.controls['password'].hasError('required');
  }

  get minLengthValid() {
    return !this.signupForm.controls['password'].hasError('minlength');
  }

  get requiresDigitValid() {
    return !this.signupForm.controls['password'].hasError('requiresDigit');
  }

  get requiresUppercaseValid() {
    return !this.signupForm.controls['password'].hasError('requiresUppercase');
  }

  get requiresLowercaseValid() {
    return !this.signupForm.controls['password'].hasError('requiresLowercase');
  }

  get requiresSpecialCharsValid() {
    return !this.signupForm.controls['password'].hasError(
      'requiresSpecialChars'
    );
  }

  checkCaptcha(captchaResponse: string) {
    this.captchaResolved =
      captchaResponse && captchaResponse.length > 0 ? true : false;
  }

  onSubmit() {
    this.submitted = true;
    if (this.signupForm.controls['generalCondition'].value === false ||
      this.signupForm.controls['dataProtection'].value === false) {
      if (
        this.signupForm.invalid ||
        this.selectedRequestAccess.length == 0 ||
        !this.captchaResolved
      ) {
        return;
      }
    }

    if (
      this.signupForm.valid &&
      this.selectedRequestAccess.length > 0 &&
      this.selectRequestAccess &&
      this.captchaResolved &&
      this.signupForm.controls['generalCondition'].value === true &&
      this.signupForm.controls['dataProtection'].value === true
    ) {
      const payload = {
        industryRoleCodes: [this.selectedIndustryRole],
        uid: this.signupForm.controls['email'].value,
        password: this.signupForm.controls['password'].value,
        firstName: this.signupForm.controls['firstName'].value,
        lastName: this.signupForm.controls['lastName'].value,
        titleCode: this.signupForm.controls['title'].value,
        accessRequestCodes: this.selectedRequestAccess,
        companyName: this.signupForm.controls['companyName'].value,
        defaultSoldTo: this.signupForm.controls['soldTo'].value,
        companyStreet: this.signupForm.controls['address1'].value,
        companyStreetNumber: this.signupForm.controls['address1'].value,
        companyAddressTwo: this.signupForm.controls['address2'].value,
        companyCity: this.signupForm.controls['city'].value,
        companyZip: this.signupForm.controls['zip'].value,
        companyCountryCode: this.signupForm.controls['country'].value,
        companyStateCode: this.signupForm.controls['state'].value,
        companyPhoneNumber: this.signupForm.controls['phone'].value,
        companyFaxNumber: this.signupForm.controls['fax'].value,
        companyWebsite: this.signupForm.controls['website'].value,
        additionalSoldToLocations: this.signupForm.controls['addSoldTo'].value,
        vatIdentificationNo: '',
        captchaCode: ''
      };

      this.registerService.signUpUser(payload).pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
        this.messageSessionService.setMessage(
          GlobalMessageType.MSG_TYPE_INFO,
          res?.message
        );

        console.log(
          'Stored Message:',
          sessionStorage.getItem('session-message') // replace with actual key
        );

        this.router.navigate(['/']);
      });
    }
  }

  patternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl) => {
      if (!control.value) {
        // if the control value is empty return no error.
        return null;
      }

      // test the value of the control against the regexp supplied.
      const valid = regex.test(control.value);

      // if true, return no error, otherwise return the error object passed in the second parameter.
      return valid ? null : error;
    };
  }

  MatchValidator(control: AbstractControl) {
    const password = control.get('password')?.value;
    const confirmPasswordControl = control.get('confirmPassword');

    if (!confirmPasswordControl) {
      return null;
    }

    const confirmPassword = confirmPasswordControl.value;

    // Check if confirm password is empty
    if (!confirmPassword || confirmPassword.trim() === '') {
      confirmPasswordControl.setErrors({ required: true });
      return { required: true };
    }

    // Check min length
    if (confirmPassword.length < 12) {
      confirmPasswordControl.setErrors({ minLength: true });
      return { minLength: true };
    }

    // Check password mismatch
    if (password !== confirmPassword) {
      confirmPasswordControl.setErrors({ mismatch: true });
      return { mismatch: true };
    }

    // Clear errors if all good
    confirmPasswordControl.setErrors(null);
    return null;
  }

  onCountryChange(ev) {
    this.registerService.getRegions(ev.target.value).pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.stateList = data;
    });
  }

  applyIndustryRoleFilter() {
    this.filteredIndustryRoles = this.pageFieldData.industryRolesWSList;
    if (this.mexicoDisable) {
      this.filteredIndustryRoles = this.filteredIndustryRoles.filter(
        role => role.code !== 'FLEET' && role.code !== 'OEM'
      );
    }
  }

  selectIndustryRole(ev) {
    this.selectedIndustryRole = ev.target.value;
    if (this.coookieService.getCookieValue('countryIsoCode') == 'MX') {
      if (this.selectedIndustryRole === 'OEM') {
        this.accessRequestsWSList =
          this.pageFieldData.accessRequestsWSList.filter(
            (item) => item.code === 'DEALER_ADB' || item.code === 'ORDER_ONLINE'
          );
      }
    } else {
      if (this.selectedIndustryRole === 'OEM') {
        this.accessRequestsWSList = this.pageFieldData.accessRequestsWSList;
      } else {
        this.accessRequestsWSList =
          this.pageFieldData.accessRequestsWSList.filter(
            (item) => item.code !== 'DEALER_ADB'
          );
      }
    }
  }

  selectRequestAccess(ev) {
    const index = this.selectedRequestAccess.indexOf(ev.target.value);
    if (index > -1) {
      this.selectedRequestAccess.splice(index, 1);
    } else {
      this.selectedRequestAccess.push(ev.target.value);
    }
  }

  openGeneralConditionDialog() {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    const dialogRef = this.dialog.open(
      GeneralConditionsComponent,
      dialogConfig
    );
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data) {
        this.signupForm.controls['generalCondition'].patchValue(data);
      }
    });
  }

  openDataProtectionDialog() {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    const dialogRef = this.dialog.open(DataProtectionComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        this.signupForm.controls['dataProtection'].patchValue(data);
      }
    });
  }
  openSoldToInfoDialog() {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.panelClass = 'sold-to-info';

    const dialogRef = this.dialog.open(SoldToInfoComponent, dialogConfig);
  }
  generalConditionClick(ev) {
    this.signupForm.controls['generalCondition'].patchValue(ev.target.checked);
  }

  dataProtectionClick(ev) {
    this.signupForm.controls['dataProtection'].patchValue(ev.target.checked);
  }

  checkExistingEmailAddress() {
    if (this.signupForm.controls['email'].value) {
      this.registerService
        .checkExistingEmail(this.signupForm.controls['email'].value).pipe(takeUntil(this.destroy$))
        .subscribe((res: any) => {
          this.isUserAlreadyRegistered = res?.message
            ? res?.message
            : '';
          this.cdf.detectChanges();
        });
    }
  }
  ngOnDestroy(): void {
    // Unsubscribe from the scroll event
    this.destroy$.next();
    this.destroy$.complete();
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }

}
